import React from 'react'
import { Grid, Card, CardActionArea, CardMedia, CardContent, CardHeader, Typography } from '@mui/material'
import ExternalLink from 'components/ExternalLink'
import ConfirmButton from 'components/ConfirmButton'
import Box from 'components/Box'
import { isMember } from 'services/user'
import { Delete, InsertPhoto, Visibility, VisibilityOff } from '@mui/icons-material'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useSnackbar } from 'hooks/redux'
import { graphql, useFragment, useLazyLoadQuery } from 'react-relay'
import { trackError } from 'services/analytics'
import { ProjectSubmissionsField_UpdateProjectSubmissionMutation } from '__generated__/ProjectSubmissionsField_UpdateProjectSubmissionMutation.graphql'
import { ProjectSubmissionsField_DeleteProjectSubmissionMutation } from '__generated__/ProjectSubmissionsField_DeleteProjectSubmissionMutation.graphql'
import { ProjectSubmissionsField_Query } from '__generated__/ProjectSubmissionsField_Query.graphql'
import { ProjectSubmissionsField_User$key } from '__generated__/ProjectSubmissionsField_User.graphql'
import { useNodes } from 'hooks/relay'
import { Nullable } from 'types/global'
import useStyles from './styles'
import AddSampleWork from './components/AddSampleWork'
import TabPlaceholder from '../../TabPlaceholder'

export interface IProjectSubmissionsFieldProps {
  editable: boolean
  userId: string
}

function renderDescription(projectSubmission: { description: Nullable<string> }) {
  if (!projectSubmission?.description) {
    return null
  }

  if (projectSubmission.description.length > 190) {
    return `${projectSubmission.description.substring(0, 189)}...`
  }

  return projectSubmission.description
}

function ProjectSubmissionsField(props: IProjectSubmissionsFieldProps) {
  const { classes } = useStyles()
  const { editable, userId } = props
  const { showSnackbarMessage } = useSnackbar()

  const data = useLazyLoadQuery<ProjectSubmissionsField_Query>(graphql`
    query ProjectSubmissionsField_Query($userId: ID!) {
      user: optionalNode(id: $userId) {
        ...ProjectSubmissionsField_User
      }
    }
  `, { userId }, {
    fetchPolicy: 'store-or-network',
  })

  const user = useFragment(graphql`
    fragment ProjectSubmissionsField_User on User {
      self
      status
      self

      projectSubmissions {
        id
        status
        url
        screenshotUrl
        description
        project {
          title
        }
      }

      ...user_isMember
    }
  `, data?.user as ProjectSubmissionsField_User$key)

  const projectSubmissions = useNodes(user?.projectSubmissions)
  const sortedSubmissions = projectSubmissions.sort((a, b) => {
    return a.project?.title?.localeCompare(b.project?.title || '') || 0
  })
  const isProfileCreationMode = Boolean(isMember(user) && user?.status === 'pending' && user?.self)

  const { execute: updateCommit } = useQuickCommit<ProjectSubmissionsField_UpdateProjectSubmissionMutation>(
    graphql`
      mutation ProjectSubmissionsField_UpdateProjectSubmissionMutation($input: UpdateProjectSubmissionInput!) {
        updateProjectSubmission(input: $input) {
          projectSubmission {
            status
          }
        }
      }
    `,
  )

  const { execute: deleteCommit } = useQuickCommit<ProjectSubmissionsField_DeleteProjectSubmissionMutation>(
    graphql`
      mutation ProjectSubmissionsField_DeleteProjectSubmissionMutation($input: DeleteProjectSubmissionInput!) {
        deleteProjectSubmission(input: $input) {
          projectSubmission {
            id @deleteRecord
          }
        }
      }
    `,
  )

  const handleItemClick = (url: Nullable<string>) => {
    if (!url) {
      return
    }

    // We could use window.open instead, but there are 2 issues. First, it may result in different behavior across browsers (in some cases, chrome does
    // not open the new content in a tab, but in a floating window instead). Second, there are security implications. Using a link to open the window is
    // simple and consistent and secure.
    let anchor = window.document.createElement('a')
    anchor.style.position = 'fixed'
    anchor.style.left = '-200px'
    anchor.href = url
    anchor.rel = 'noreferrer noopener'
    anchor.target = '_blank'
    window.document.body.appendChild(anchor)
    anchor.click()
    window.document.body.removeChild(anchor)
  }

  const handleToggleVisibility = (id: string, isPublic: boolean) => {
    try {
      updateCommit({
        input: {
          projectSubmissionId: id,
          status: isPublic ? 'private' : 'public', // toggle the visibility
        },
      })

      showSnackbarMessage('Sample work updated', { level: 'success' })
    } catch (err) {
      trackError(err)
      showSnackbarMessage('An error occurred while updating sample', { level: 'error' })
    }
  }

  const handleDelete = (id: string) => {
    try {
      deleteCommit({
        input: {
          projectSubmissionId: id,
        },
      })
      showSnackbarMessage('Sample work deleted', { level: 'success' })
    } catch (err) {
      trackError(err)
      showSnackbarMessage('An error occurred while deleting sample', { level: 'error' })
    }
  }

  return (
    <Box data-cy="profile-sample-work">
      <Grid container spacing={2}>
        {sortedSubmissions.map((projectSubmission, i) => (
          <Grid key={projectSubmission.id} item xs={12} md={6}>
            <Card className={classes.card} data-cy={`sample-work-${i}`}>
              <CardHeader
                title={(
                  <Typography variant="h4" data-cy="title">
                    {projectSubmission.project?.title}
                  </Typography>
                )}
                action={editable && (
                  <React.Fragment>
                    <ConfirmButton
                      icon
                      tooltip={(projectSubmission.status === 'public') ? 'Sample work is public - click to make it private' : 'Sample work is private - click to make it public'}
                      dialogTitle="Toggle visibility"
                      dialogMessage={(projectSubmission.status === 'public') ? 'This will hide your sample work from your profile. Continue?' : 'This will make your sample work show up on your profile to the public. Continue?'}
                      onClick={() => handleToggleVisibility(projectSubmission.id, projectSubmission.status === 'public')}
                      data-cy="toggle-visibility"
                    >
                      {(projectSubmission.status === 'public') ? <Visibility /> : <VisibilityOff />}
                    </ConfirmButton>

                    <ConfirmButton
                      icon
                      tooltip="Delete sample work submission"
                      dialogTitle="Delete sample work submission"
                      onClick={() => handleDelete(projectSubmission.id)}
                      data-cy="delete"
                    >
                      <Delete />
                    </ConfirmButton>
                  </React.Fragment>
                )}
              />

              <CardActionArea className={classes.cardActionArea} onClick={() => handleItemClick(projectSubmission.url)}>
                <CardMedia
                  className={classes.screenshot}
                  image={projectSubmission.screenshotUrl || undefined}
                >
                  {!projectSubmission.screenshotUrl && (
                    <div className={classes.placeholder}>
                      <InsertPhoto className={classes.placeholderIcon} />
                    </div>
                  )}
                </CardMedia>

                <CardContent className={classes.cardContent}>
                  <div className={classes.description} data-cy="description">
                    {renderDescription(projectSubmission)}
                  </div>

                  <div className={classes.link}>
                    <ExternalLink href={projectSubmission.url} showExternalIcon data-cy="url" />
                  </div>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {projectSubmissions.length === 0 && (
        <TabPlaceholder
          title="No work samples posted yet"
        />
      )}

      {editable && (
        <AddSampleWork
          isProfileCreationMode={isProfileCreationMode}
        />
      )}
    </Box>
  )
}

export default React.memo(ProjectSubmissionsField)
